/**
 * A live case's row in the queue's preview: what needs a person there,
 * counted by Needs you's sections, and what release waits on; then the first
 * few items in Needs you's order, answered in place with the workbench's own
 * card — a review issue opens the draft at it. The rest wait in Needs you.
 * Read from the case (the row's entity, a report) when the row is selected,
 * and again after each answer.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Anchor, Badge, Button, Card, Group, Loader, Stack, Text } from '@mantine/core';

import * as Sentry from '@sentry/react';

import { InterventionCard } from '@/components/Workbench/InterventionCard';
import {
  liveDraftKey,
  needsYouItems,
  needsYouSections,
  workOrder,
  type NeedsYouItem,
} from '@/components/Workbench/needsYouOrder';
import { RecordIndexProvider } from '@/components/Workbench/recordIndex';
import type { ConditionalResult } from '@/services/http';
import { vnextApiClient } from '@/services/vnext-api';
import type { InboxItem } from '@/types/inbox';
import {
  liveDraft,
  sectionCount,
  SECTIONS,
  type ChecklistItem,
  type EvidenceIndexEntry,
  type Intervention,
  type ReviewIssue,
  type WorkbenchDocument,
} from '@/types/workbench';
import { toError } from '@/utils/errors';
import { caseAsks } from './queueFormat';

/** How many items the preview answers in place; the rest open in Needs you. */
export const CARDS_SHOWN = 3;

type Loaded =
  | {
      status: 'ready';
      asks: Intervention[];
      issues: ReviewIssue[];
      documents: WorkbenchDocument[];
      checklist: ChecklistItem[];
      index: EvidenceIndexEntry[];
      draftId: string | null;
    }
  | { status: 'loading' }
  | { status: 'failed' };

/** A read's data, or nothing where it failed or wasn't modified. */
function dataOf<T>(settled: PromiseSettledResult<ConditionalResult<T>>, fallback: T): T {
  return settled.status === 'fulfilled' && settled.value !== 'not-modified'
    ? settled.value.data
    : fallback;
}

interface QueueCaseAsksProps {
  item: InboxItem;
  onAnswered: () => void | Promise<void>;
}

export function QueueCaseAsks({ item, onAnswered }: Readonly<QueueCaseAsksProps>) {
  const reportId = item.entityId;
  const family = item.codenameFamilyCached;
  const summary = caseAsks(item);
  const [loaded, setLoaded] = useState<Loaded>({ status: 'loading' });
  const [reads, setReads] = useState(0);
  const navigate = useNavigate();
  const workbench = family ? `/cases/${encodeURIComponent(family)}?item=rep:${reportId}` : null;

  // What a card points at opens in the case's workbench, in focus; a record
  // opens beside Needs you.
  const openTarget = useMemo(
    () =>
      workbench
        ? (target: { type: 'checklist' | 'evidence'; id: string }) =>
            navigate(
              `${workbench}&${
                target.type === 'checklist'
                  ? `view=brief&checklist=${target.id}`
                  : `view=needs_you&record=${target.id}`
              }`
            )
        : undefined,
    [navigate, workbench]
  );
  const openRecord = useMemo(
    () => (openTarget ? (id: string) => openTarget({ type: 'evidence', id }) : null),
    [openTarget]
  );
  const openSetup = useMemo(
    () => (workbench ? () => navigate(`${workbench}&view=setup`) : undefined),
    [navigate, workbench]
  );
  // A rework is sent from Partner work on the case, at its ask.
  const openAsk = useMemo(
    () =>
      workbench
        ? (id: string) => navigate(`${workbench}&view=needs_you&intervention=${id}`)
        : undefined,
    [navigate, workbench]
  );

  useEffect(() => {
    const controller = new AbortController();
    const options = { signal: controller.signal };
    void Promise.allSettled([
      vnextApiClient.listWorkbenchInterventions(reportId, options),
      vnextApiClient.listWorkbenchReviewIssues(reportId, options),
      vnextApiClient.listWorkbenchDocuments(reportId, options),
      vnextApiClient.listWorkbenchChecklist(reportId, options),
      vnextApiClient.getWorkbenchEvidenceIndex(reportId, options),
      vnextApiClient.getWorkbenchHeader(reportId, options),
    ]).then(([asks, issues, documents, checklist, index, header]) => {
      if (controller.signal.aborted) {
        return;
      }
      // The asks are what the preview answers; the rest only order and
      // name them.
      if (asks.status === 'rejected' || asks.value === 'not-modified') {
        if (asks.status === 'rejected') {
          Sentry.captureException(toError(asks.reason));
        }
        setLoaded({ status: 'failed' });
        return;
      }
      const read =
        header.status === 'fulfilled' && header.value !== 'not-modified' ? header.value.data : null;
      const docs = dataOf(documents, []);
      setLoaded({
        status: 'ready',
        asks: asks.value.data,
        issues: dataOf(issues, []),
        documents: docs,
        checklist: dataOf(checklist, []),
        index: dataOf(index, []),
        // The live draft whose issues Needs you lists: the header's, else
        // the built Word draft among the documents.
        draftId: read?.needsYou.draftId ?? liveDraft(docs)?.id ?? null,
      });
    });
    return () => controller.abort();
  }, [reportId, reads]);

  const answered = useCallback(async () => {
    setReads((n) => n + 1);
    await onAnswered();
  }, [onAnswered]);

  const order = useMemo((): NeedsYouItem[] => {
    if (loaded.status !== 'ready') {
      return [];
    }
    // The case's subjects in the order its checklist lists them.
    const subjects = [
      ...new Set(loaded.checklist.flatMap((c) => (c.subjectId ? [c.subjectId] : []))),
    ];
    return workOrder(
      needsYouSections(
        needsYouItems(
          loaded.asks,
          loaded.issues,
          liveDraftKey(loaded.draftId, loaded.documents),
          loaded.checklist
        ),
        subjects
      )
    );
  }, [loaded]);
  const draftId = loaded.status === 'ready' ? loaded.draftId : null;
  const shown = order.slice(0, CARDS_SHOWN);
  const rest = order.length - shown.length;
  const needsYou = workbench ? `${workbench}&view=needs_you` : null;

  return (
    <RecordIndexProvider index={loaded.status === 'ready' ? loaded.index : null} open={openRecord}>
      <Stack gap="xs" data-testid="queue-case-asks">
        {summary && (
          <Group gap={6} data-testid="queue-case-asks-counts">
            {summary.releaseWaitsOn !== null && summary.releaseWaitsOn > 0 && (
              <Badge size="sm" variant="light" color="red" style={{ textTransform: 'none' }}>
                Release waits on {summary.releaseWaitsOn}
              </Badge>
            )}
            {summary.blocking > 0 && (
              <Badge size="sm" variant="light" color="red" style={{ textTransform: 'none' }}>
                {summary.blocking} holding up a run
              </Badge>
            )}
            {SECTIONS.filter((s) => summary.bySection[s]).map((section) => (
              <Badge
                key={section}
                size="sm"
                variant="light"
                color="gray"
                style={{ textTransform: 'none' }}
              >
                {sectionCount(section, summary.bySection[section] ?? 0)}
              </Badge>
            ))}
          </Group>
        )}

        {loaded.status === 'loading' && <Loader size="xs" />}
        {loaded.status === 'failed' && (
          <Text size="xs" c="dimmed">
            What needs you couldn&apos;t be loaded here — open it in the workbench.
          </Text>
        )}
        {loaded.status === 'ready' && order.length === 0 && (
          <Text size="xs" c="dimmed" data-testid="queue-case-asks-none">
            Nothing is open any more — the queue will catch up.
          </Text>
        )}

        {shown.map((entry) =>
          entry.kind === 'ask' ? (
            <InterventionCard
              key={entry.id}
              intervention={entry.ask}
              reportId={reportId}
              onAnswered={answered}
              onOpenTarget={openTarget}
              onOpenSetup={openSetup}
              onOpenAsk={openAsk}
            />
          ) : (
            <Card
              key={entry.id}
              withBorder
              padding="sm"
              radius="md"
              data-testid={`queue-issue-${entry.id}`}
            >
              <Stack gap={6}>
                <Group gap={6}>
                  <Badge
                    size="xs"
                    variant="light"
                    color={entry.issue.severity === 'error' ? 'red' : 'yellow'}
                    style={{ textTransform: 'none' }}
                  >
                    {entry.issue.severity === 'error' ? 'Error' : 'Warning'}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    The draft{entry.issue.page !== null ? ` · page ${entry.issue.page}` : ''}
                  </Text>
                </Group>
                <Text size="sm">{entry.issue.message}</Text>
                {workbench && draftId && (
                  <Button
                    size="xs"
                    variant="default"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() =>
                      navigate(`${workbench}&view=document&doc=${draftId}&issue=${entry.id}`)
                    }
                    data-testid={`queue-issue-open-${entry.id}`}
                  >
                    Open it in the draft
                  </Button>
                )}
              </Stack>
            </Card>
          )
        )}

        {rest > 0 && (
          <Text size="sm" data-testid="queue-case-asks-more">
            and {rest} more{' '}
            {needsYou ? (
              <Anchor size="sm" onClick={() => navigate(needsYou)}>
                in Needs you
              </Anchor>
            ) : (
              'in Needs you'
            )}
          </Text>
        )}
      </Stack>
    </RecordIndexProvider>
  );
}
